// Sprint 6-optimum §4.3 — единый источник цвета статуса маркера (3 тира).
// Дисплей-тир выводится из (clinical_status, optimum_status) по резолвер-словарю
// §1. Потребляют: карточка (Главная/Маркеры), деталь, ShareCard, органы (Трек B).
// `improved` НЕ тир на этих поверхностях — улучшение показывается дельтой.

import type { ClinicalStatus, EnrichedBiomarkerStatus, OptimumStatus } from '@/api/types';

export type DisplayTier = 'norm' | 'attention' | 'bad' | 'no-range';

/**
 * Резолвер-словарь §1:
 *   out_of_range              → bad       🔴
 *   in_range + suboptimal     → attention 🟠
 *   in_range + optimal/null   → norm      🟢
 *   no_range                  → no-range  нейтральный
 */
export function displayTier(
  clinical: ClinicalStatus,
  optimum: OptimumStatus = null,
): DisplayTier {
  if (clinical === 'out_of_range') return 'bad';
  if (clinical === 'no_range') return 'no-range';
  return optimum === 'suboptimal' ? 'attention' : 'norm';
}

/**
 * HEAL-22 — мостик экрана ревью («Проверьте и сохраните») в 3-тир модель.
 *
 * До сохранения бэкенд отдаёт не `clinical_status`, а более дробный
 * `EnrichedBiomarkerStatus` (below/above/improved/…). Раньше у экрана ревью был
 * СВОЙ маппинг цвета, где below/above красились 🟠 — из-за этого маркер вне нормы
 * был оранжевым до сохранения и красным после. Теперь тир один на всё приложение:
 *
 *   below | above           → out_of_range → 🔴 bad
 *   normal | improved       → in_range     → 🟢 norm (или 🟠 при optimum=suboptimal)
 *   no_range | unknown | unclassified      → нейтральный
 *
 * `improved` на ревью недостижим (энричер зовёт классификатор без prev-значения),
 * но маппится честно — на случай, если prev когда-нибудь начнут прокидывать.
 */
export function reviewDisplayTier(
  status: EnrichedBiomarkerStatus,
  optimum: OptimumStatus = null,
): DisplayTier {
  return displayTier(reviewClinicalStatus(status), optimum);
}

function reviewClinicalStatus(status: EnrichedBiomarkerStatus): ClinicalStatus {
  switch (status) {
    case 'below':
    case 'above':
      return 'out_of_range';
    case 'normal':
    case 'improved':
      return 'in_range';
    case 'no_range':
    case 'unknown':
    case 'unclassified':
      return 'no_range';
    default: {
      // Мобильный бандл отстаёт от бэка на сборку Codemagic, а ответ upload'а не
      // валидируется рантайм-схемой — новый статус с бэка сюда доедет как строка
      // вне union. Красим нейтральным: 🟢 на неизвестном статусе означал бы
      // «всё хорошо» на маркере, про который мы ничего не знаем.
      // `never` держит compile-time exhaustiveness: расширят union — упадёт tsc.
      const exhaustive: never = status;
      void exhaustive;

      return 'no_range';
    }
  }
}

/** Полоска/бар слева от карточки. */
export const STRIPE_BY_TIER: Record<DisplayTier, string> = {
  norm: 'bg-norm',
  attention: 'bg-attention',
  bad: 'bg-bad',
  'no-range': 'bg-no-range',
};

/** Левая цветная граница карточки (экран ревью). Те же токены, что у полоски. */
export const BORDER_LEFT_BY_TIER: Record<DisplayTier, string> = {
  norm: 'border-l-norm',
  attention: 'border-l-attention',
  bad: 'border-l-bad',
  'no-range': 'border-l-no-range',
};

/**
 * Цвет числа значения.
 *
 * D-26: у красного роль текста отдана отдельному тиру `--bad-text` (#dc2626,
 * 4,83:1). Заливочный #ef4444 даёт 3,76 и порог AA не проходит, но остаётся
 * у полоски и точки — там контраст текста не требуется. Зелёный и янтарный
 * как текст порог тоже не проходят (2,28 и 2,15), но их исправление меняет
 * облик заметно и вынесено отдельным решением (D-29).
 */
export const VALUE_TEXT_BY_TIER: Record<DisplayTier, string> = {
  norm: 'text-norm',
  attention: 'text-attention',
  bad: 'text-bad-text',
  'no-range': 'text-gray-700',
};

/** Точка-индикатор статуса (деталь/ShareCard). */
export const DOT_BG_BY_TIER: Record<DisplayTier, string> = {
  norm: 'bg-norm',
  attention: 'bg-attention',
  bad: 'bg-bad',
  'no-range': 'bg-gray-400',
};

/** Пилюля (history-hint / бейдж статуса). */
export const PILL_BY_TIER: Record<DisplayTier, string> = {
  norm: 'bg-norm/15 text-norm',
  attention: 'bg-attention/15 text-attention',
  bad: 'bg-bad/15 text-bad',
  'no-range': 'bg-gray-200 text-gray-600',
};

/**
 * D2 (Q2): блок «Что значит ваш результат» показывает optimum_text ВМЕСТО
 * normal-инсайта ТОЛЬКО на 🟠 suboptimal и только если текст непуст (§0 —
 * «оранжевый не молчит»). На 🟢/🔴 — обычный per-marker AI-инсайт.
 */
export function showsOptimumText(optimum: OptimumStatus, text: string | null): boolean {
  return optimum === 'suboptimal' && text !== null && text.trim() !== '';
}

const TIER_SORT_PRIORITY: Record<DisplayTier, number> = {
  bad: 0,
  attention: 1,
  norm: 2,
  'no-range': 3,
};

/**
 * Сортировка «проблемные сверху» по дисплей-тиру: 🔴 → 🟠 → 🟢 → нейтральный.
 * Сортировка стабильная (Array.prototype.sort в ES2019+), поэтому внутри тира
 * сохраняется исходный клинический порядок из `biomarkers.sort_order`.
 *
 * HEAL-22: тир-функция вынесена параметром — экран ревью до сохранения оперирует
 * `EnrichedBiomarkerStatus`, а не `clinical_status`, но обязан ранжировать так же.
 */
export function sortByTierWith<T>(items: T[], tierOf: (item: T) => DisplayTier): T[] {
  return [...items].sort((a, b) => TIER_SORT_PRIORITY[tierOf(a)] - TIER_SORT_PRIORITY[tierOf(b)]);
}

/** Сортировка по `clinical_status` + `optimum_status` (Главная, Маркеры, органы). */
export function sortByTier<
  T extends { clinical_status: ClinicalStatus; optimum_status?: OptimumStatus },
>(items: T[]): T[] {
  return sortByTierWith(items, (item) =>
    displayTier(item.clinical_status, item.optimum_status ?? null),
  );
}

type StatusItem = { clinical_status: ClinicalStatus; optimum_status?: OptimumStatus };

/**
 * «Заголовочный» тир категории (точка-индикатор аккордеона): наивысший по
 * тревожности присутствующий тир — `bad > attention > norm > no-range`.
 * Пусто / всё no_range → `no-range`. Мостит category-агрегат в 3-тир модель
 * (раньше accordion-заголовок был 2-тирным и красил 🟠-only категорию зелёной).
 */
export function categoryHeadlineTier<T extends StatusItem>(items: T[]): DisplayTier {
  const tiers = items.map((i) => displayTier(i.clinical_status, i.optimum_status ?? null));
  if (tiers.includes('bad')) return 'bad';
  if (tiers.includes('attention')) return 'attention';
  if (tiers.includes('norm')) return 'norm';
  return 'no-range';
}

/** Категория авто-раскрывается, если в ней есть 🔴 или 🟠 (важное — сразу). */
export function categoryNeedsAttention<T extends StatusItem>(items: T[]): boolean {
  const tier = categoryHeadlineTier(items);
  return tier === 'bad' || tier === 'attention';
}

/** Точка-индикатор заголовка категории. norm/no-range — бренд-оттенки (не card-токены). */
export const CATEGORY_DOT_BY_TIER: Record<DisplayTier, string> = {
  bad: 'bg-bad',
  attention: 'bg-attention',
  norm: 'bg-primary',
  'no-range': 'bg-gray-300',
};

/**
 * D-11 — статус несёт не только цвет.
 *
 * Принцип «цвет = статус» нарушает WCAG 1.4.1: красно-зелёную пару не
 * различают около 8% мужчин, а точка-кружок одинаковой формы во всех
 * четырёх состояниях не даёт им ничего. Форма различается сама по себе,
 * в том числе на чёрно-белом скриншоте.
 *
 * Там, где в строке есть место для слова (история измерений), ставим слово,
 * а не глиф: слово читается лучше значка.
 */
export const STATUS_GLYPH_BY_TIER: Record<DisplayTier, { glyph: string; label: string }> = {
  bad: { glyph: '▲', label: 'есть отклонения' },
  attention: { glyph: '◆', label: 'требуют внимания' },
  norm: { glyph: '●', label: 'все в норме' },
  'no-range': { glyph: '■', label: 'без оценки' },
};

/** Слово статуса — для строк, где есть место для текста. */
export const STATUS_WORD_BY_TIER: Record<DisplayTier, string> = {
  bad: 'вне нормы',
  attention: 'не оптимально',
  norm: 'в норме',
  'no-range': 'без оценки',
};

/** Цвет глифа. Те же токены, что у точки, которую он заменил. */
export const GLYPH_TEXT_BY_TIER: Record<DisplayTier, string> = {
  bad: 'text-bad',
  attention: 'text-attention',
  norm: 'text-primary',
  'no-range': 'text-gray-400',
};
